import React, { useRef, useState, useEffect } from "react";
import ReactCrop, { centerCrop, makeAspectCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { RotateCcw, RotateCw } from "lucide-react";

// Aspect ratio matches final 8cm x 7cm output (width / height = 8/7).
const ASPECT = 8 / 7;
// Output image pixel dimensions (high enough for crisp printing at 300dpi).
const OUT_W = 944; // ~8cm @ 300dpi
const OUT_H = 826; // ~7cm @ 300dpi

const rotateImageDataUrl = (dataUrl, deg) =>
  new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const w = img.naturalWidth;
      const h = img.naturalHeight;
      const canvas = document.createElement("canvas");
      const rad = (deg * Math.PI) / 180;
      const absSin = Math.abs(Math.sin(rad));
      const absCos = Math.abs(Math.cos(rad));
      canvas.width = Math.round(w * absCos + h * absSin);
      canvas.height = Math.round(w * absSin + h * absCos);
      const ctx = canvas.getContext("2d");
      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate(rad);
      ctx.drawImage(img, -w / 2, -h / 2);
      resolve(canvas.toDataURL("image/jpeg", 0.92));
    };
    img.src = dataUrl;
  });

const ImageCropper = ({ open, src, onCancel, onSave }) => {
  const imgRef = useRef(null);
  const [crop, setCrop] = useState(null);
  const [completed, setCompleted] = useState(null);
  const [fixedAspect, setFixedAspect] = useState(true);
  const [workingSrc, setWorkingSrc] = useState("");

  useEffect(() => {
    if (open) {
      setWorkingSrc(src || "");
      setCrop(null);
      setCompleted(null);
    } else {
      setWorkingSrc("");
    }
  }, [open, src]);

  const rotate = async (deg) => {
    if (!workingSrc) return;
    const rotated = await rotateImageDataUrl(workingSrc, deg);
    setCrop(null);
    setCompleted(null);
    setWorkingSrc(rotated);
  };

  const onImageLoad = (e) => {
    const { width, height } = e.currentTarget;
    if (fixedAspect) {
      const c = centerCrop(makeAspectCrop({ unit: "%", width: 90 }, ASPECT, width, height), width, height);
      setCrop(c);
      const px = {
        unit: "px",
        x: (c.x / 100) * width,
        y: (c.y / 100) * height,
        width: (c.width / 100) * width,
        height: (c.height / 100) * height,
      };
      setCompleted(px);
    } else {
      setCrop({ unit: "%", x: 5, y: 5, width: 90, height: 90 });
    }
  };

  const handleSave = async () => {
    if (!imgRef.current || !completed) return;
    const image = imgRef.current;
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    const cw = completed.width * scaleX;
    const ch = completed.height * scaleY;
    const cx = completed.x * scaleX;
    const cy = completed.y * scaleY;

    const canvas = document.createElement("canvas");
    canvas.width = OUT_W;
    canvas.height = OUT_H;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, OUT_W, OUT_H);
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(image, cx, cy, cw, ch, 0, 0, OUT_W, OUT_H);
    const dataUrl = canvas.toDataURL("image/jpeg", 0.9);
    onSave(dataUrl);
  };

  const handleUseOriginal = () => {
    // Save the (possibly rotated) image untouched (no cropping).
    onSave(workingSrc || src);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onCancel(); }}>
      <DialogContent className="max-w-3xl rounded-sm">
        <DialogHeader>
          <DialogTitle>Crop &amp; Rotate image</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-2 rounded-sm border border-[#E5E7EB] bg-slate-50 px-3 py-2 text-xs md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" className="rounded-sm" onClick={() => rotate(-90)} data-testid="rotate-left-button">
              <RotateCcw className="mr-1 h-3.5 w-3.5" /> Rotate Left
            </Button>
            <Button variant="outline" size="sm" className="rounded-sm" onClick={() => rotate(90)} data-testid="rotate-right-button">
              <RotateCw className="mr-1 h-3.5 w-3.5" /> Rotate Right
            </Button>
          </div>
          <div className="flex items-center gap-2">
            <div className="text-right">
              <div className="font-semibold text-slate-700">Lock to print size (8 × 7 cm)</div>
              <div className="text-slate-500">Off = crop freely or use full image.</div>
            </div>
            <Switch
              checked={fixedAspect}
              onCheckedChange={(v) => {
                setFixedAspect(v);
                setCrop(null);
                setCompleted(null);
                if (imgRef.current) onImageLoad({ currentTarget: imgRef.current });
              }}
              data-testid="crop-aspect-toggle"
            />
          </div>
        </div>

        <div className="flex max-h-[58vh] justify-center overflow-auto rounded-sm border border-[#E5E7EB] bg-slate-100 p-3">
          {workingSrc && (
            <ReactCrop
              crop={crop || undefined}
              onChange={(c) => setCrop(c)}
              onComplete={(c) => setCompleted(c)}
              aspect={fixedAspect ? ASPECT : undefined}
              keepSelection
            >
              <img
                ref={imgRef}
                src={workingSrc}
                onLoad={onImageLoad}
                alt="to crop"
                style={{ maxHeight: "52vh", maxWidth: "100%" }}
                data-testid="crop-source-image"
              />
            </ReactCrop>
          )}
        </div>

        <DialogFooter className="flex-col gap-2 sm:flex-row sm:justify-between">
          <Button variant="ghost" onClick={handleUseOriginal} className="rounded-sm" data-testid="use-original-button">
            Use Original (no crop)
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onCancel} className="rounded-sm" data-testid="cancel-crop-button">
              Cancel
            </Button>
            <Button onClick={handleSave} className="rounded-sm ep-amber hover:ep-amber" data-testid="save-crop-button">
              Save Cropped Image
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ImageCropper;
